/**
 * Zustand Store Debugging Utilities
 *
 * 개발 환경에서 zustand store들을 DevTools와 window에 바인딩하여 디버깅할 수 있게 합니다.
 *
 * @example
 * // Store에 DevTools 적용하기
 * import { create } from 'zustand'
 * import { withDevTools } from '@/utils/storeDebug'
 *
 * export const useMyStore = create(
 *   withDevTools(
 *     (set) => ({
 *       count: 0,
 *       increment: () => set((state) => ({ count: state.count + 1 })),
 *     }),
 *     'MyStore', // DevTools에서 표시될 이름
 *   ),
 * )
 *
 * @example
 * // 브라우저 콘솔에서 사용하기
 * // 1. 현재 상태 확인
 * window.__ZUSTAND_STORES__.useAuthStore.getState()
 *
 * // 2. 상태 변경
 * window.__ZUSTAND_STORES__.useTankStore.setState({ tanks: {} })
 *
 * // 3. 상태 구독
 * window.__ZUSTAND_STORES__.useGroupStore.subscribe((state) => console.log(state))
 *
 * // 4. Redux DevTools Extension 사용
 * // Chrome/Firefox 확장 프로그램 설치 후 자동으로 연결됩니다.
 */

import { devtools } from 'zustand/middleware'
import type { StateCreator } from 'zustand'

// Store imports
import { useAuthStore } from '@/store/auth/useAuthStore'
import useHomeAddGroupStore from '@/store/home/useHomeAddGroupStore'
import useHomeAddBatchStore from '@/store/home/useHomeAddBatchStore'
import { useTankStore } from '@/store/tank/useTankStore'
import useHomeUpdateStore from '@/store/home/useHomeUpdateStore'
import useHomeGradeStore from '@/store/home/useHomeGradeStore'
import { useBatchStore } from '@/store/batch/useBatchStore'
import { useSelectTankStore } from '@/store/selectTank/useSelectTankStore'
import { useAvgDistDateStore } from '@/store/chart/useAvgDistDateStore'
import { useGroupStore } from '@/store/group/useGroupStore'
import { useTankInfoStageStore } from '@/store/tankInfo/useTankInfoStageStore'
import { useSchoolStore } from '@/store/school/useSchoolStore'
import useCustomizeLiveStore from '@/store/live/useCustomizeLiveStore'
import useChartStore from '@/store/chart/useChartStore'
import useHomeStockStore from '@/store/home/useHomeStockStore'
import { useTankInfoModalStore } from '@/store/tankInfo/useTankInfoModalStore'
import useHomeEditBatchStore from '@/store/home/useHomeEditBatchStore'

const isDevelopment = import.meta.env.MODE === 'development' || import.meta.env.MODE === 'local'

// Window 타입 확장
declare global {
  interface Window {
    __ZUSTAND_STORES__?: {
      useAuthStore: typeof useAuthStore
      useHomeAddGroupStore: typeof useHomeAddGroupStore
      useHomeAddBatchStore: typeof useHomeAddBatchStore
      useTankStore: typeof useTankStore
      useHomeUpdateStore: typeof useHomeUpdateStore
      useHomeGradeStore: typeof useHomeGradeStore
      useBatchStore: typeof useBatchStore
      useSelectTankStore: typeof useSelectTankStore
      useAvgDistDateStore: typeof useAvgDistDateStore
      useGroupStore: typeof useGroupStore
      useTankInfoStageStore: typeof useTankInfoStageStore
      useSchoolStore: typeof useSchoolStore
      useCustomizeLiveStore: typeof useCustomizeLiveStore
      useChartStore: typeof useChartStore
      useHomeStockStore: typeof useHomeStockStore
      useTankInfoModalStore: typeof useTankInfoModalStore
      useHomeEditBatchStore: typeof useHomeEditBatchStore
    }
    __REDUX_DEVTOOLS_EXTENSION__?: unknown
  }
}

/**
 * DevTools를 사용하는 store creator wrapper
 * 개발 환경에서만 DevTools를 활성화합니다.
 */
export const withDevTools = <T>(
  storeCreator: StateCreator<T, [], []>,
  storeName: string,
): StateCreator<T, [], []> => {
  if (isDevelopment) {
    return devtools(storeCreator, { name: storeName }) as unknown as StateCreator<T, [], []>
  }
  return storeCreator
}

/**
 * Window에 모든 store들을 바인딩합니다.
 * 개발 환경에서만 동작합니다.
 */
export const bindStoresToWindow = () => {
  if (!isDevelopment || typeof window === 'undefined') {
    return
  }

  // Store들을 window에 바인딩
  window.__ZUSTAND_STORES__ = {
    useAuthStore,
    useHomeAddGroupStore,
    useHomeAddBatchStore,
    useTankStore,
    useHomeUpdateStore,
    useHomeGradeStore,
    useBatchStore,
    useSelectTankStore,
    useAvgDistDateStore,
    useGroupStore,
    useTankInfoStageStore,
    useSchoolStore,
    useCustomizeLiveStore,
    useChartStore,
    useHomeStockStore,
    useTankInfoModalStore,
    useHomeEditBatchStore,
  }

  // 콘솔에 안내 메시지 출력
  console.log(
    '%c🔍 Zustand Stores Debugging',
    'color: #4CAF50; font-size: 16px; font-weight: bold;',
  )
  console.log(
    '%c모든 zustand store들이 window.__ZUSTAND_STORES__에 바인딩되었습니다.',
    'color: #2196F3; font-size: 12px;',
  )
  console.log(
    '%c사용 예시:',
    'color: #FF9800; font-size: 12px; font-weight: bold;',
  )
  console.log('  window.__ZUSTAND_STORES__.useAuthStore.getState()')
  console.log('  window.__ZUSTAND_STORES__.useTankStore.setState({ ... })')
  console.log('  window.__ZUSTAND_STORES__.useGroupStore.subscribe((state) => console.log(state))')
  console.log('')
  console.log('📦 사용 가능한 stores:', Object.keys(window.__ZUSTAND_STORES__))
}
